import React from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Download } from "lucide-react";
import { SectionCard, SectionHeader, EmptyState } from "./ui/DashboardUI";

const isImageUrl = (url = "") =>
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((url.split("?")[0] || "").toLowerCase());

/** Single upload tile */
const UploadTile = ({ item, onOpen }) => {
  const navigate = useNavigate();
  const thumb = Array.isArray(item.files) && item.files[0] ? item.files[0] : null;
  const showImage = thumb && isImageUrl(thumb);

  const goToDetails = (e) => {
    e?.stopPropagation?.();
    const doctorId = item.doctorId || item.ownerId || item.uid || "unknown";
    const researchId = item.id;
    if (doctorId && researchId) {
      navigate(`/research/${encodeURIComponent(doctorId)}/${encodeURIComponent(researchId)}`);
      return;
    }
    // If IDs missing, fallback to external handler (if provided), else open first file
    if (typeof onOpen === "function") return onOpen(item);
    const first = item?.files?.[0];
    if (first) window.open(first, "_blank", "noopener,noreferrer");
  };

  return (
    <article
      className="rl-tile hover-lift"
      role="button"
      tabIndex={0}
      onClick={goToDetails}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goToDetails(e)}
    >
      <div className="rl-thumb" aria-hidden>
        {showImage ? (
          <img src={thumb} alt="" className="rl-img" />
        ) : (
          <FileText className="rl-docIcon" />
        )}
      </div>

      <div className="rl-body">
        <h3 className="rl-title" title={item.title}>{item.title}</h3>
        {!!item.description && (
          <p className="rl-desc" title={item.description}>{item.description}</p>
        )}

        <div className="rl-meta">
          <span className="rl-chip">
            {(item.files?.length || 0)} file{(item.files?.length || 0) !== 1 ? "s" : ""}
          </span>
          <span className="rl-download">
            <Download className="rl-dlIcon" />
            {item.downloads || 0} downloads
          </span>
        </div>

        <button type="button" className="rl-cta" onClick={goToDetails}>
          View details
        </button>
      </div>

      <style>{`
        .rl-tile {
          display:flex; flex-direction:column; background:#fff;
          border:1px solid #e5e7eb; border-radius:12px; overflow:hidden;
          transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
          cursor: pointer;
        }
        .rl-thumb { width:100%; height:160px; background:linear-gradient(135deg,#f6f7fb,#eef1f6);
          position:relative; overflow:hidden; border-bottom:1px solid #eef2f7; display:grid; place-items:center; }
        .rl-img { width:100%; height:100%; object-fit:cover; transition: transform .25s ease; }
        .rl-tile:hover .rl-img { transform: scale(1.02); }
        .rl-docIcon { width:44px; height:44px; color:#9ca3af; }
        .rl-body { padding:12px; display:grid; gap:10px; }
        .rl-title { margin:0; font-size:14.5px; font-weight:700; color:#0f172a; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .rl-desc { margin:0; font-size:12.5px; color:#6b7280; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
        .rl-meta { display:flex; align-items:center; justify-content:space-between; gap:10px; font-size:12px; color:#6b7280; }
        .rl-chip { background:#f3f4f6; border:1px solid #e5e7eb; padding:4px 8px; border-radius:999px; font-weight:700; }
        .rl-download { background:#eaf1ff; color:#1d4ed8; border:1px solid #dbeafe; padding:4px 8px; border-radius:999px; font-weight:700; display:inline-flex; align-items:center; gap:6px; }
        .rl-dlIcon { width:12px; height:12px; }
        .rl-cta {
          margin-top:2px; width:100%; background:linear-gradient(135deg,#3b82f6,#6366f1);
          color:#fff; padding:8px 12px; border-radius:10px; border:none;
          font-size:13px; font-weight:700; cursor:pointer;
          box-shadow:0 10px 18px -12px rgba(59,130,246,.45);
          transition: filter .15s ease, transform .15s ease;
        }
        .rl-cta:hover { filter:brightness(1.02); transform:translateY(-1px); }
        @media (max-width:560px){ .rl-thumb{height:140px;} .rl-body{padding:10px; gap:8px;} .rl-title{font-size:14px;} .rl-desc{font-size:12px;} }
      `}</style>
    </article>
  );
};

const ResearchLibrary = ({ uploads = [] }) => {
  return (
    <SectionCard variant="flat">
      
      <div className="rl-wrap">
        {uploads.length === 0 ? (
          <EmptyState title="No uploads yet" subtitle="Share your research to build credibility" />
        ) : (
          <div className="rl-grid">
            {uploads.map((item) => (
              <UploadTile key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
      <style>{`
        .rl-wrap { padding: 8px 0 0; }
        .rl-grid {
          display:grid;
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          gap:16px;
        }
        @media (min-width:1100px){
          .rl-grid { gap:20px; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); }
        }
      `}</style>
    </SectionCard>
  );
};

export default React.memo(ResearchLibrary);
